import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

const LINKS = [
  ["/tax-services", "nav_tax_services"],
  ["/how-it-works", "nav_how"],
  ["/vat-health-check", "nav_health"],
  ["/vat-compliance", "nav_compliance"],
  ["/vat-advisory", "nav_advisory"],
  ["/zatca-audit", "nav_zatca"],
  ["/pricing", "nav_pricing"],
  ["/knowledge", "nav_knowledge"],
  ["/contact", "nav_contact"],
];

export function Logo({ light }) {
  const { t } = useI18n();
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${light ? "bg-white/10" : "bg-[hsl(217_63%_15%)]"}`}>
        <ShieldCheck className="h-5 w-5" style={{ color: "hsl(41 72% 55%)" }} />
      </div>
      <div className="leading-tight">
        <div className={`font-display font-extrabold text-[15px] tracking-tight ${light ? "text-white" : "text-[hsl(217_63%_15%)]"}`}>{t("brand")}</div>
        <div className={`text-[10px] tracking-wide ${light ? "text-white/60" : "text-muted-foreground"}`}>{t("brand_sub")}</div>
      </div>
    </Link>
  );
}

export function LangToggle({ light }) {
  const { toggle, lang } = useI18n();
  return (
    <button onClick={toggle} data-testid="language-toggle-button"
      className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-1.5 border transition-colors ${light ? "border-white/20 text-white hover:bg-white/10" : "border-border hover:bg-secondary"}`}>
      <Globe className="h-4 w-4" />
      {lang === "ar" ? "English" : "عربي"}
    </button>
  );
}

export default function Navbar() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 gc-glass border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map(([to, key]) => (
            <Link key={to} to={to} data-testid={`nav-${key}`}
              className="px-3 py-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors rounded-md">
              {t(key)}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <LangToggle />
          {user ? (
            <Button data-testid="nav-dashboard-btn" onClick={() => nav("/app")} className="rounded-full bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">{t("dashboard")}</Button>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => nav("/login")} className="rounded-full">{t("login")}</Button>
              <Button data-testid="nav-cta-btn" onClick={() => nav("/diagnostic")} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">{t("cta_check")}</Button>
            </>
          )}
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <LangToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="p-2"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="top" className="pt-10">
              <div className="flex flex-col gap-1">
                {LINKS.map(([to, key]) => (
                  <Link key={to} to={to} onClick={() => setOpen(false)} className="px-3 py-3 text-base font-medium border-b border-border">{t(key)}</Link>
                ))}
                <div className="flex gap-2 mt-4">
                  {user ? (
                    <Button className="flex-1" onClick={() => { setOpen(false); nav("/app"); }}>{t("dashboard")}</Button>
                  ) : (
                    <>
                      <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); nav("/login"); }}>{t("login")}</Button>
                      <Button className="flex-1 bg-[hsl(158_64%_28%)]" onClick={() => { setOpen(false); nav("/diagnostic"); }}>{t("cta_check")}</Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
